import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getApiUrl } from '@/lib/config';
import { useChatHistoryStore } from '@/store/chat';
import { useActiveChatStore } from '@/store/active-chat';
import { storesApi } from '@/lib/api';

export interface User {
  id: number;
  username: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
  updated_at?: string;
  last_login?: string;
  timezone?: string;
  // Computed property for convenience
  is_admin?: boolean;
  // Connected-as metadata (from JWT token)
  is_connected_as?: boolean;
  original_user_id?: number;
  original_username?: string;
  original_role?: string;
}

export interface StoreInfo {
  store_id: number;
  store_url: string;
  store_name: string;
  role: 'owner' | 'manager' | 'member'; // Updated role model
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in?: number;
}

interface AuthState {
  // State
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isDisconnecting: boolean;  // Guard flag to prevent concurrent disconnect calls
  error: string | null;
  viewAsUser: boolean;  // When true, hide admin UI elements (for testing user view)
  
  // Store state
  stores: StoreInfo[];
  currentStoreId: number | null;
  attentionItems: {
    join_requests: number;
  };

  // Actions
  setUser: (user: User) => void;
  setTokens: (tokens: AuthTokens) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  login: (tokens: AuthTokens, user: User) => void;
  logout: () => void;
  clearError: () => void;
  
  // Connected-as getters (derived from user object)
  isConnectedAs: () => boolean;
  getOriginalUser: () => { id: number; username: string; role: string } | null;
  hasAdminAccess: () => boolean;
  canConnectAs: () => boolean;
  
  // View as user toggle (hides admin UI without changing identity)
  toggleViewAsUser: () => void;
  shouldShowAdminUI: () => boolean;

  // Connected-as actions (token-based)
  startConnectAs: (username: string) => Promise<void>;
  stopConnectAs: () => Promise<void>;
  
  // Store actions
  fetchStores: () => Promise<void>;
  switchStore: (storeId: number) => Promise<void>;
  getCurrentStore: () => StoreInfo | null;
  isStoreManager: () => boolean;
  fetchAttentionItems: () => Promise<void>;
}

const mapApiUserToStore = (data: any): User => {
  const role = data.role ?? 'user';
  const originalRole = data.original_role;

  return {
    id: data.id,
    username: data.username,
    email: data.email ?? '',
    name: data.name ?? '',
    role,
    created_at: data.created_at ?? '',
    updated_at: data.updated_at,
    last_login: data.last_login,
    timezone: data.timezone,
    is_connected_as: data.is_connected_as,
    original_user_id: data.original_user_id,
    original_username: data.original_username,
    original_role: originalRole,
    is_admin: role === 'admin' || originalRole === 'admin',
  };
};

const fetchUserWithToken = async (accessToken: string): Promise<User | null> => {
  try {
    const response = await fetch(getApiUrl('auth/me'), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error('Failed to fetch user info after token update:', error);
      return null;
    }

    const data = await response.json();
    return mapApiUserToStore(data);
  } catch (error) {
    console.error('Failed to fetch user info after token update:', error);
    return null;
  }
};

const clearCachedUserPreferences = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem('user-preferences');
  } catch {
    // ignore
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      isDisconnecting: false,
      error: null,
      viewAsUser: false,
      stores: [],
      currentStoreId: null,
      attentionItems: {
        join_requests: 0,
      },

      // Actions
      setUser: (user) => 
        set({ 
          user: user
            ? { ...user, is_admin: user.role === 'admin' || user.original_role === 'admin' }
            : null, 
          isAuthenticated: !!user 
        }),

      setTokens: (tokens) =>
        set((state) => {
          if (!tokens) {
            return { tokens: null, isAuthenticated: false };
          }

          const mergedTokens: AuthTokens = {
            ...(state.tokens ?? {
              access_token: '',
              refresh_token: '',
              token_type: 'bearer',
            }),
            ...tokens,
            refresh_token: tokens.refresh_token || state.tokens?.refresh_token || '',
            token_type: tokens.token_type || state.tokens?.token_type || 'bearer',
          };

          return {
            tokens: mergedTokens,
            isAuthenticated: true,
          };
        }),

      setLoading: (isLoading) => 
        set({ isLoading }),

      setError: (error) => 
        set({ error }),

      login: (tokens, user) => {
        // Set a flag in sessionStorage to indicate fresh login
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('just_logged_in', 'true');
        }

        set({
          tokens: {
            ...tokens,
            refresh_token: tokens.refresh_token || '',
            token_type: tokens.token_type || 'bearer',
          },
          user: { ...user, is_admin: user.role === 'admin' || user.original_role === 'admin' },
          isAuthenticated: true,
          error: null,
          isLoading: false
        });
      },

      logout: () => {
        // Clear active conversation before logging out
        useChatHistoryStore.getState().setActiveConversation(null);

        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          error: null,
          isLoading: false,
          stores: [],
          currentStoreId: null
        });
      },

      clearError: () => 
        set({ error: null }),

      // Connected-as getters (derived from user object in token)
      isConnectedAs: () => {
        const state = get();
        return state.user?.is_connected_as === true;
      },

      getOriginalUser: () => {
        const state = get();
        if (!state.user?.is_connected_as) return null;

        return {
          id: state.user.original_user_id!,
          username: state.user.original_username!,
          role: state.user.original_role!
        };
      },

      hasAdminAccess: () => {
        const state = get();
        return (
          state.user?.role === 'admin' ||
          state.user?.original_role === 'admin'
        );
      },

      canConnectAs: () => {
        return get().hasAdminAccess();
      },

      // View as user toggle - hides admin UI without changing identity
      toggleViewAsUser: () => {
        set((state) => ({ viewAsUser: !state.viewAsUser }));
      },

      shouldShowAdminUI: () => {
        const state = get();
        if (state.viewAsUser) return false;  // Hide admin UI when toggle is on
        return state.hasAdminAccess();
      },

      // Connected-as actions (token-based)
      startConnectAs: async (username: string) => {
        const state = get();
        
        // Validate admin
        if (!state.canConnectAs()) {
          throw new Error('Only admins can connect as other users');
        }
        
        try {
          // Call backend to get connected-as token
          const url = getApiUrl(`admin/connect-as/start/${username}`);
          console.log('[ConnectAs] Calling:', url);
          
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${state.tokens?.access_token}`,
              'Content-Type': 'application/json'
            }
          });

          console.log('[ConnectAs] Response status:', response.status);

          if (!response.ok) {
            const error = await response.json();
            console.error('[ConnectAs] Error response:', error);
            throw new Error(error.detail || 'Failed to connect as user');
          }

          const { access_token, refresh_token } = await response.json();

          // Decode token to get immediate user info (simple base64 decode of JWT payload)
          const payload = JSON.parse(atob(access_token.split('.')[1]));

          const placeholderUser: User = {
            id: payload.user_id,
            username: payload.sub,
            name: '',
            email: '',
            role: payload.role,
            created_at: '',
            // Connected-as metadata
            is_connected_as: payload.is_connected_as,
            original_user_id: payload.original_user_id,
            original_username: payload.original_username,
            original_role: payload.original_role,
            is_admin: payload.role === 'admin' || payload.original_role === 'admin'
          };

          // Clear any persisted per-user UI state before switching identity
          // so the new user doesn't inherit the previous user's active conversation.
          useChatHistoryStore.getState().resetSession();
          useActiveChatStore.getState().reset();
          clearCachedUserPreferences();

          // Update state with new token and placeholder user immediately
          set({
            tokens: {
              access_token,
              token_type: 'bearer',
              // Use backend-issued refresh token; fall back to existing if not provided.
              refresh_token: refresh_token || state.tokens?.refresh_token || '',
            },
            user: placeholderUser,
            isAuthenticated: true
          });

          // Fetch fresh user details with the new token
          const updatedUser = await fetchUserWithToken(access_token);
          if (updatedUser) {
            set({ user: updatedUser });
          }

          return;
        } catch (error) {
          console.error('Failed to connect as user:', error);
          throw error;
        }
      },

      stopConnectAs: async () => {
        const state = get();

        // Prevent concurrent disconnect calls (race condition guard)
        if (state.isDisconnecting) {
          console.log('[Auth] Disconnect already in progress, skipping duplicate call');
          return;
        }

        // If not connected as another user, treat as already disconnected (idempotent)
        if (!state.isConnectedAs()) {
          console.log('[Auth] Not connected as another user, nothing to disconnect');
          return;
        }


        set({ isDisconnecting: true });

        try {
          // Call backend to get normal admin token
          const response = await fetch(getApiUrl('admin/connect-as/stop'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${state.tokens?.access_token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            const error = await response.json();
            // If backend says we're not connected, treat as already disconnected (idempotent)
            if (error.detail?.includes('Not currently connected')) {
              console.log('[Auth] Backend says not connected, refreshing user state');
              // Refresh user state from /auth/me to sync with reality
              const meResponse = await fetch(getApiUrl('auth/me'), {
                headers: { 'Authorization': `Bearer ${state.tokens?.access_token}` }
              });
              if (meResponse.ok) {
                const userData = await meResponse.json();
                set({
                  user: mapApiUserToStore(userData),
                  isDisconnecting: false
                });
              }
              return;
            }
            throw new Error(error.detail || 'Failed to disconnect');
          }

          const { access_token, refresh_token } = await response.json();

          // Decode token to get immediate admin info
          const payload = JSON.parse(atob(access_token.split('.')[1]));

          const placeholderAdmin: User = {
            id: payload.user_id,
            username: payload.sub,
            name: '',
            email: '',
            role: payload.role,
            created_at: '',
            is_admin: payload.role === 'admin' || payload.original_role === 'admin'
          };

          // Clear any persisted per-user UI state before switching identity
          useChatHistoryStore.getState().resetSession();
          useActiveChatStore.getState().reset();
          clearCachedUserPreferences();

          // Update state immediately with new token and placeholder admin user
          set({
            tokens: {
              access_token,
              token_type: 'bearer',
              // Use backend-issued refresh token; fall back to existing if not provided.
              refresh_token: refresh_token || state.tokens?.refresh_token || '',
            },
            user: placeholderAdmin,
            isAuthenticated: true,
            isDisconnecting: false
          });

          // Fetch fresh admin details
          const updatedUser = await fetchUserWithToken(access_token);
          if (updatedUser) {
            set({ user: updatedUser });
          }

          return;
        } catch (error) {
          console.error('Failed to disconnect:', error);
          set({ isDisconnecting: false });
          throw error;
        }
      },

      // Store actions
      fetchStores: async () => {
        const state = get();
        if (!state.tokens?.access_token) return;

        try {
          const stores: StoreInfo[] = await storesApi.getMyStores();
          
          // Set current store to first one if not already set OR if current store doesn't belong to user
          const currentStoreExists = stores.some(s => s.store_id === state.currentStoreId);
          const currentStoreId = (state.currentStoreId && currentStoreExists) 
            ? state.currentStoreId 
            : (stores.length > 0 ? stores[0].store_id : null);
          
          set({ stores, currentStoreId });
        } catch (error) {
          console.error('Failed to fetch stores:', error);
        }
      },

      switchStore: async (storeId: number) => {
        const state = get();
        if (!state.tokens?.access_token) return;

        try {
          const data = await storesApi.switchStore(storeId);

          const { access_token, store_id, store_name } = data;

          // Clear active chat and history before switching to new store context
          // This ensures the new store doesn't inherit conversations from the old store
          useActiveChatStore.getState().reset();
          useChatHistoryStore.getState().resetSession();

          // Update tokens with new store-scoped token
          set({
            tokens: {
              access_token,
              token_type: 'bearer',
              refresh_token: state.tokens?.refresh_token || '',
            },
            currentStoreId: store_id
          });

          console.log(`Switched to store: ${store_name}`);
        } catch (error) {
          console.error('Failed to switch store:', error);
          throw error;
        }
      },

      getCurrentStore: () => {
        const state = get();
        if (!state.currentStoreId || state.stores.length === 0) return null;
        return state.stores.find(s => s.store_id === state.currentStoreId) || null;
      },

      isStoreManager: () => {
        const state = get();
        const store = state.stores.find(s => s.store_id === state.currentStoreId);
        return store?.role === 'manager';
      },

      fetchAttentionItems: async () => {
        if (!get().isAuthenticated) return;
        const token = get().tokens?.access_token;
        if (!token) return;
        try {
          const summary = await storesApi.getPendingActionsSummary();
          set({ attentionItems: summary });
        } catch {
          // Silent — background poll, non-critical
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        viewAsUser: state.viewAsUser,
        stores: state.stores,
        currentStoreId: state.currentStoreId,
      }),
    }
  )
);
